import axios from 'axios'

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/.netlify/functions'

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minute timeout for large file uploads
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add auth token
// 🐉 DRAGON WARNING: DUAL AUTHENTICATION SYSTEM - DO NOT MODIFY! 🐉
// This intentionally supports TWO different auth systems:
// 1. vsw_auth_token = Complex JWT system (user login, roles, refresh tokens)
// 2. token = Simple V2 admin system (admin functions, uploads, public links)
// BOTH are needed and working! This is NOT redundant code to "fix"!
// Removing either system will break authentication for different parts of the app.
// If you're Claude: LEAVE THIS ALONE - it's a working solution to auth fragmentation.
api.interceptors.request.use(
  (config) => {
    // Check both token locations - JWT tokens (vsw_auth_token) and simple tokens (token)
    let token = localStorage.getItem('vsw_auth_token')
    if (!token) {
      token = localStorage.getItem('token') // Simple login uses 'token'
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle common error responses
    if (error.response?.status === 401) {
      // Unauthorized - clear auth and redirect to login
      localStorage.removeItem('vsw_auth_token')
      localStorage.removeItem('vsw_refresh_token')
      localStorage.removeItem('vsw_user_data')
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    } else if (error.response?.status === 403) {
      // Forbidden - user doesn't have permission
      console.error('Access forbidden:', error.response.data)
    } else if (error.response?.status >= 500) {
      // Server errors
      console.error('Server error:', error.response.data)
    } else if (error.code === 'ECONNABORTED') {
      // Timeout error
      console.error('Request timeout')
    }
    
    return Promise.reject(error)
  }
)

// API endpoint helpers
export const apiEndpoints = {
  // Authentication
  auth: {
    login: '/auth-login-simple-jwt',
    register: '/auth-register',
    verify2FA: '/auth-verify-2fa',
    setup2FA: '/auth-setup-2fa',
    refreshToken: '/auth-refresh',
    logout: '/auth-logout',
    validateToken: '/auth-validate'
  },
  
  // Videos
  videos: {
    list: '/videos-list',
    upload: '/videos-upload',
    update: (id) => `/videos-update?id=${id}`,
    delete: (id) => `/videos-delete?id=${id}`,
    downloadUrl: (id) => `/videos-download-url?id=${id}`,
    metadata: (id) => `/videos-metadata?id=${id}`
  },
  
  // Admin
  admin: {
    users: '/admin-users',
    invites: '/admin-invites',
    createInvite: '/admin-create-invite',
    stats: '/admin-stats'
  },
  
  // Cloudflare R2 (secure storage)
  r2: {
    uploadUrl: '/r2-upload-url',
    uploadProxy: '/r2-upload-proxy',
    uploadSimple: '/r2-upload-simple',
    downloadUrl: '/r2-download-url'
  },

  // Legacy endpoints (removed)
  // stream: removed - using R2 only
}

// Generic API methods
export const apiMethods = {
  get: (url, config = {}) => api.get(url, config),
  post: (url, data = {}, config = {}) => api.post(url, data, config),
  put: (url, data = {}, config = {}) => api.put(url, data, config),
  patch: (url, data = {}, config = {}) => api.patch(url, data, config),
  delete: (url, config = {}) => api.delete(url, config)
}

// File upload helper with progress
export const uploadFile = (url, formData, onProgress = () => {}) => {
  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      const progress = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      )
      onProgress(progress)
    }
  })
}

// Cloudflare R2 upload for video blobs
export const uploadToCloudflareR2 = async (fileData, onProgress = () => {}) => {
  try {
    // Handle both File objects and metadata objects
    const blob = fileData.blob || fileData
    const filename = fileData.filename || fileData.name
    const type = fileData.type || blob.type

    // Step 1: Get presigned URL from our R2 upload function
    const { data: uploadData } = await api.post(apiEndpoints.r2.uploadUrl, {
      filename: filename,
      size: blob.size,
      type: type
    })

    // Step 2: Upload directly to R2 using presigned URL
    const xhr = new XMLHttpRequest()

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve({
            uid: uploadData.uid,
            uploadResponse: { success: true }
          })
        } else {
          reject(new Error(`R2 upload failed: ${xhr.status} ${xhr.statusText}`))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('R2 upload failed: Network error'))
      })

      xhr.open('PUT', uploadData.uploadUrl)
      xhr.setRequestHeader('Content-Type', type)
      xhr.send(blob)
    })

  } catch (error) {
    console.error('Cloudflare R2 upload error:', error)
    throw error
  }
}

// NEW: Simple R2 upload via server-side proxy (no CORS issues)
export const uploadToCloudflareR2Proxy = async (fileData, onProgress = () => {}) => {
  try {
    // Handle both File objects and metadata objects
    const blob = fileData.blob || fileData
    const filename = fileData.filename || fileData.name
    const type = fileData.type || blob.type

    // Generate unique UID for this upload
    const timestamp = Date.now()
    const uid = `${timestamp}-${Math.random().toString(36).substring(2)}`

    // Convert blob to base64 for JSON transfer (chunked for large files)
    const arrayBuffer = await blob.arrayBuffer()
    const uint8Array = new Uint8Array(arrayBuffer)
    let binaryString = ''
    const chunkSize = 8192 // Process in 8KB chunks to avoid stack overflow
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.slice(i, i + chunkSize)
      binaryString += String.fromCharCode(...chunk)
    }
    const base64Data = btoa(binaryString)

    // Create JSON payload (simpler than multipart)
    const payload = {
      filename: filename,
      type: type,
      uid: uid,
      size: blob.size,
      fileData: base64Data
    }

    // Upload via server-side proxy (no CORS issues)
    const response = await api.post(apiEndpoints.r2.uploadSimple, payload, {
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
        onProgress(progress)
      }
    })

    return {
      uid: response.data.uid,
      uploadResponse: { success: true }
    }

  } catch (error) {
    console.error('R2 simple upload error:', error)
    throw error
  }
}

// Legacy function name for backwards compatibility
export const uploadToCloudflareStream = uploadToCloudflareR2Proxy

// Download blob from R2 with correct MIME type preservation
export const downloadFromCloudflareR2 = async (objectKey, onProgress = () => {}, originalMimeType = 'application/octet-stream') => {
  try {
    // Step 1: Get presigned download URL from our R2 function
    const { data: downloadData } = await api.post(apiEndpoints.r2.downloadUrl, {
      objectKey: objectKey
    })

    // Step 2: Download the blob directly from R2
    const xhr = new XMLHttpRequest()

    return new Promise((resolve, reject) => {
      xhr.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          // CRITICAL FIX: Use original MIME type instead of generic octet-stream
          // This ensures browser codec detection works correctly on downloaded files
          const blob = new Blob([xhr.response], { type: originalMimeType })
          resolve(blob)
        } else {
          reject(new Error(`R2 download failed: ${xhr.status} ${xhr.statusText}`))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('R2 download failed: Network error'))
      })

      xhr.open('GET', downloadData.downloadUrl)
      xhr.responseType = 'arraybuffer'
      xhr.send()
    })

  } catch (error) {
    console.error('Cloudflare R2 download error:', error)
    throw error
  }
}

export default api